import React from "react";
import { Text } from "../../ui/Text/Text";
import { cn } from "../../../lib/cn";

/**
 * LoadingState component for displaying loading states
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.variant='spinner'] - Loading variant (spinner, skeleton, pulse)
 * @param {string} [props.size='md'] - Size (sm, md, lg)
 * @param {string} [props.text] - Loading text
 * @param {boolean} [props.fullScreen=false] - Full screen overlay
 * @param {number} [props.rows=3] - Number of skeleton rows (skeleton variant only)
 * @returns {JSX.Element}
 *
 * @example
 * // Spinner
 * <LoadingState variant="spinner" text="Loading..." />
 *
 * @example
 * // Skeleton loader
 * <LoadingState variant="skeleton" rows={5} />
 *
 * @example
 * // Full screen loading
 * <LoadingState variant="spinner" text="Loading data..." fullScreen />
 *
 * @example
 * // Pulse animation
 * <LoadingState variant="pulse" />
 */
export function LoadingState({
  className,
  variant = "spinner",
  size = "md",
  text,
  fullScreen = false,
  rows = 3,
}) {
  // Size classes
  const sizeClasses = {
    sm: "w-6 h-6",
    md: "w-10 h-10",
    lg: "w-16 h-16",
  };

  const textSizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
  };

  // Spinner component
  const Spinner = () => (
    <div
      className={cn(
        "border-4 border-gray-200 border-t-primary rounded-full animate-spin",
        sizeClasses[size]
      )}
      role="status"
      aria-label="Loading"
    />
  );

  // Skeleton component
  const Skeleton = () => (
    <div className="space-y-3 w-full animate-pulse">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="space-y-2">
          <div
            className="h-4 bg-gray-200 rounded"
            style={{ width: `${Math.random() * 30 + 70}%` }}
          />
          {i % 2 === 0 && (
            <div
              className="h-4 bg-gray-200 rounded"
              style={{ width: `${Math.random() * 20 + 50}%` }}
            />
          )}
        </div>
      ))}
    </div>
  );

  // Pulse component (for cards/images)
  const Pulse = () => (
    <div className="w-full h-32 bg-gray-200 rounded animate-pulse" />
  );

  // Content based on variant
  const renderContent = () => {
    switch (variant) {
      case "skeleton":
        return <Skeleton />;
      case "pulse":
        return <Pulse />;
      case "spinner":
      default:
        return (
          <div className="flex flex-col items-center justify-center gap-4">
            <Spinner />
            {text && (
              <Text className={cn("text-gray-600", textSizeClasses[size])}>
                {text}
              </Text>
            )}
          </div>
        );
    }
  };

  // Full screen wrapper
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <Spinner />
          {text && (
            <Text className={cn("text-gray-600", textSizeClasses[size])}>
              {text}
            </Text>
          )}
        </div>
      </div>
    );
  }

  // Regular wrapper
  return (
    <div
      className={cn(
        "flex items-center justify-center py-8",
        variant === "skeleton" && "w-full",
        className
      )}
    >
      {renderContent()}
    </div>
  );
}

/**
 * Pre-defined LoadingState variants
 */
LoadingState.Spinner = (props) => <LoadingState variant="spinner" {...props} />;
LoadingState.Skeleton = (props) => (
  <LoadingState variant="skeleton" {...props} />
);
LoadingState.Pulse = (props) => <LoadingState variant="pulse" {...props} />;
LoadingState.FullScreen = (props) => (
  <LoadingState variant="spinner" fullScreen {...props} />
);
